import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';
import DOMPurify from 'isomorphic-dompurify';
import i18next from 'i18next';
import React, { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { changeLang } from '../../redux/actions/appSettings.actions';
import { openSnackBar } from '../../redux/actions/snackbar.actions';
import { getApiUrl } from '../../utils/apiUrls';
import { apiErrorHandler } from '../../utils/errorHandler';
import { checkPassword, replaceTranslationStringWithLink, sanitiseInput, stringPurifer } from '../../utils/helperFn';
import CustomToolTip from '../custom-tooltip/CustomTooltip.component';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { replaceDynamicString, inputValidation } from '../../utils/helperFn';
import CustomCheckbox from '../global/CustomCheckbox.component';
import ReCAPTCHA from 'react-google-recaptcha';

const initial_state = {
    firstName: {
        value: '',
        isRequired: true,
        msg: null
    },
    lastName: {
        value: '',
        msg: null
    },
    middleName: {
        value: '',
        msg: null
    },
    email: {
        value: '',
        isRequired: true,
        msg: null
    },
    password: {
        value: '',
        isRequired: true,
        msg: null
    },
    passwordVisible: false,
    passwordConfirm: {
        value: '',
        isRequired: true,
        msg: null
    },
    passwordConfirmVisible: false,
    acceptTnc: {
        value: false,
        isRequired: true,
        msg: null
    }
};

const tncHtToCheck = 250;

const SignupForm = ({ t }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const envState = useSelector(state => state.env);
    const [searchParams] = useSearchParams();
    const getLang = searchParams.get('lang');
    const getRefId = searchParams.get('refId');
    const tncRef = useRef(null);
    const state = useSelector(state => state);
    const [crfData, setCrfData] = React.useState({
        data: null,
        showCrf: false,
        isSaveBtnLoading: false,
    });
    const hasInitialDataFetched = React.useRef(false);
    const recaptchaRef = useRef(null);
    const captchaWidget = React.useMemo(() => {
        if (envState?.RECAPTCHA?.ENABLE_RECAPTCHA) {
            return (
                <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={envState?.RECAPTCHA?.SITE_KEY}
                    size="invisible"
                    className="cm-recaptcha"
                />
            );
        }
        return null;
    }, [envState?.RECAPTCHA?.ENABLE_RECAPTCHA]);

    const [tncHt, setTncHt] = React.useState(null);

    const [isLoading, setIsLoading] = React.useState(false);
    const [formVal, setFormVal] = React.useState(initial_state);
    
    React.useEffect(() => {
        if(!!getLang && !!i18next?.isInitialized) {
            setTimeout(() => {
                i18next.changeLanguage(getLang);
                // i18next.reloadResources([getLang]);
                dispatch(changeLang(getLang))
            }, 500)
        }
    }, [i18next]);

    React.useEffect(() => {
        if(getRefId) getCandidateConsentDetails()
    }, [getRefId]);

    const triggerCaptchaAndSignup = async () => {
        if (envState?.RECAPTCHA?.ENABLE_RECAPTCHA) {
            const token = await recaptchaRef.current.executeAsync();
            recaptchaRef.current?.reset();
            if (!token) {
                dispatch(openSnackBar({ msg: t('captcha_verification_failed'), type: 'error' }));
                setIsLoading(false);
                return;
            }
            encProcess(token);
        } else {
            encProcess(null);
        }
    };

    React.useLayoutEffect(() => {
        if(tncRef.current && crfData.data && hasInitialDataFetched.current) {
            const height = tncRef.current.offsetHeight;
            setTncHt(height);
            hasInitialDataFetched.current = false;
        }
    }, [crfData.data, hasInitialDataFetched]);

    const getCandidateConsentDetails = async () => {
      try {
        const res = await axios.post(`${getApiUrl('getCandidateAllPreloginConsentDetails')}?consentType=PRE_LOGIN_CONSENT&refId=${getRefId}`, {
            title: null,
            description: null,
            langId: null,
            consentDetailsDtoList: null,
            showConsentPage: null,
            email: null,
            defaultConsent: null,
        });

        if(res.data) {
          setCrfData({
            ...crfData,
            ...res.data,
            data: res.data?.consentDetailsDtoList,
            isLoading: false,
          });
          hasInitialDataFetched.current = true;
        }

      } catch (error) {
        const errObj = apiErrorHandler(error);

        dispatch(openSnackBar({
            msg: t(errObj.statusText),
            type: 'error'
        }))
      }
    }

    const handleSaveCrfConsent = async () => {
        try {
            //Check if all mandatory consents are checked
            const allMandatoryChecked = crfData.data.every(item => {
                if(item.mandatory && item.checkbox) {
                    return item.checked === true;
                }
                return true;
            });

            if(!allMandatoryChecked) {
                // Add error message for unchecked mandatory consents
                const updatedFormVal = crfData.data.map(item => {
                    if(item.mandatory && item.checkbox && !item.checked) {
                        return {
                            ...item,
                            msg: "this_consent_is_mandatory"
                        }
                    }
                    return item;
                });
                setCrfData({...crfData, data: updatedFormVal});
                
                if(tncRef.current?.offsetHeight > tncHtToCheck) {
                    setTimeout(() => {
                    //Add code to scrollTo closest ".text-danger" class inside tncRef
                    const errorElement = tncRef.current.querySelector(".text-danger");
                    if (errorElement) {
                        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
                    }
                    }, 100)
                }

                dispatch(openSnackBar({
                    msg: t("accept_all_mandatory_consents_to_proceed"),
                    type: "error"
                }))
                return;
            }

            setIsLoading(true);

            const dataToSend = {
                title: crfData?.title,
                description: crfData?.description,
                langId: crfData?.langId,
                consentDetailsDtoList: crfData.data.map(item => ({
                    orderId: item.orderId,
                    mandatory: item.mandatory,
                    enable: item.enable,
                    consentMessage: item.consentMessage,
                    modified: item.modified,
                    id: item.id,
                    checked: !item.checkbox ? true : item.checked,
                    checkbox: item.checkbox,
                    checkedAndSubmittedConsentIds: item.checkedAndSubmittedConsentIds,
                    unCheckedAndSubmittedConsentIds: item.unCheckedAndSubmittedConsentIds,
                    deleteDisable: item.deleteDisable,
                    mandatoryChange: item.mandatoryChange
                })),
                showConsentPage: crfData?.showConsentPage,
                email: formVal.email.value,
                defaultConsent: crfData?.defaultConsent,
            }

            // console.log("dataToSend", dataToSend);
            // setIsLoading(false);
            // return;

            //API call to save consent details
            const res = await axios.post(`${getApiUrl('saveCandidatePreloginConsentDetails')}?consentType=PRE_LOGIN_CONSENT&refId=${getRefId}`, dataToSend);

            if(res.data) {
                triggerCaptchaAndSignup();
            }
        } catch (error) {
            setIsLoading(false);
            const errObj = apiErrorHandler(error);

            dispatch(openSnackBar({
                msg: t(errObj.statusText),
                type: 'error'
            }))
        }
    }

    const handleFieldChange = (e, fieldName) => {
        setFormVal({
            ...formVal,
            [fieldName]: {
                ...formVal[fieldName],
                value: e.target.value,
                msg: null
            }
        })
    }

    const handleToggleField = (fieldName) => {
        setFormVal({
            ...formVal,
            [fieldName]: {
                ...formVal[fieldName],
                value: !formVal[fieldName].value,
            }
        })
    }

    const handlePassVisiblity = (fieldName) => {
        setFormVal({
            ...formVal,
            [fieldName]: !formVal[fieldName]
        })
    }

    const signUp = (data, lang) => async (dispatch, getState) => {
        try {
            setIsLoading(true);
            data.firstName = data.firstName.replace(/ +(?= )/g,'');
            data.lastName = data.lastName.replace(/ +(?= )/g,'');
            data.middleName = data.middleName.replace(/ +(?= )/g,'');
            data.email = data.email.replace(/ +(?= )/g,'');

            const signUpData = await axios.post(`${getApiUrl('signUp')}${`?lang=${!!lang ? lang : 'en'}`}`, data);
    
            if(signUpData.data.status === "SUCCESS") {
                setIsLoading(false);
                dispatch(openSnackBar({
                    msg: t(signUpData.data.message),
                    type: 'success'
                }));
                

                if(signUpData.data.signupId) {
                    navigate(`/verify-account?id=${signUpData.data.signupId}${!!getLang ? `&lang=${getLang}` : ''}${getRefId ? `&refId=${getRefId}`  : '' }`, {
                        state: { email: data.email }
                    });
                }
            } else {
                throw Error(signUpData.data);
            }
        } catch (err) {
            setIsLoading(false);
            const errObj = apiErrorHandler(err);

            dispatch(openSnackBar({
                msg: t(errObj.statusText),
                type: 'error'
            }))
        }
    }

    const handleValidation = () => {
        let currentFormVal = JSON.parse(JSON.stringify(formVal));
        if(!!currentFormVal.firstName.value && !!currentFormVal.password.value && !!currentFormVal.passwordConfirm.value ) {
        try {   
                stringPurifer(currentFormVal.firstName.value);
                stringPurifer(currentFormVal.middleName.value);
                stringPurifer(currentFormVal.lastName.value);
                stringPurifer(currentFormVal.password.value);
                stringPurifer(currentFormVal.passwordConfirm.value);
            } catch (error) { 
                currentFormVal.firstName.msg = "";
                currentFormVal.middleName.msg = "";
                currentFormVal.lastName.msg = "";
                currentFormVal.password.msg = "";
                currentFormVal.passwordConfirm.msg = "";
                const errObj = apiErrorHandler(error);
                setFormVal({
                    ...formVal,
                    ...currentFormVal,
                });

                dispatch(openSnackBar({
                    msg: t(errObj.statusText),
                    type: 'error'
                }))
                return
            }
        }         
        

      // FIRST NAME
      currentFormVal.firstName.msg = inputValidation(
        currentFormVal.firstName.value,
        true,
        100,
        t("input_field_required"),
        replaceDynamicString(t("maximum_limit_input_field"), [t("first_name")]),
        "regexA",
        t("invalid_format_name")
      );

      // MIDDLE NAME
      currentFormVal.middleName.msg = inputValidation(
        currentFormVal.middleName.value,
        false,
        100,
        t("input_field_required"),
        replaceDynamicString(t("maximum_limit_input_field"), [t("middle_name")]),
        "regexA",
        t("invalid_format_name")
      );

      // LAST NAME
      currentFormVal.lastName.msg = inputValidation(
        currentFormVal.lastName.value,
        false,
        100,
        t("input_field_required"),
        replaceDynamicString(t("maximum_limit_input_field"), [t("last_name")]),
        "regexA",
        t("invalid_format_name")
      );

      //Email Validation
      currentFormVal.email.msg = inputValidation(
        currentFormVal.email.value,
        true,
        100,
        t("email_required"),
        replaceDynamicString(t("maximum_limit_input_field"), [t("email")]),
        "regexB",
        t("valid_email_address")
      );

      //Password Validation
      currentFormVal.password.msg = inputValidation(
        currentFormVal.password.value,
        true,
        100,
        t("sign_up_password_required"),
        replaceDynamicString(t("maximum_limit_input_field"), [t("password")]),
        "regexC",
        t("valid_password")
      );

      currentFormVal.passwordConfirm.msg = inputValidation(
        currentFormVal?.passwordConfirm?.value,
        true,
        100,
        t("sign_up_password_required"),
        replaceDynamicString(t("maximum_limit_input_field"), [
          t("confirm_password"),
        ]),
        "regexC",
        t("valid_password")
      );
      if (
        !currentFormVal.passwordConfirm.msg &&
        currentFormVal.passwordConfirm.value !== currentFormVal.password.value
      ) {
        currentFormVal.passwordConfirm.msg = t("passwords_not_match");
      }

      if (
        !!currentFormVal.firstName.msg ||
        !!currentFormVal.middleName.msg ||
        !!currentFormVal.lastName.msg ||
        !!currentFormVal.email.msg ||
        !!currentFormVal.password.msg ||
        !!currentFormVal.passwordConfirm.msg ||
        !!currentFormVal.email.msg
      ) {
        setFormVal({
          ...formVal,
          ...currentFormVal,
        });
        return;
      }

      // API CALL
      if(!isLoading) {
        if (crfData.data) {
            handleSaveCrfConsent();
        } else {
            setIsLoading(true);
            triggerCaptchaAndSignup();
        }
      }
    };

    const encProcess = async (recaptchaToken) => {
        try {
            const getKey = await axios.get(getApiUrl('getSk'));
            if(!!getKey.data) {
                setIsLoading(false);
                const JSEncrypt = window.JSEncrypt;

                //RSA Public Key
                const rsaKey = getKey.data.key;
                const passToEncrypt = formVal.password.value;
                const confirmPassToEncrypt = formVal.passwordConfirm.value;
                const skid = getKey.data.id;

                //encrypt AES key with RSA public key
                var rsaEncrypt = new JSEncrypt({default_key_size: 2048});
                rsaEncrypt.setPublicKey(rsaKey);
                var rsaEncryptedAesKey = rsaEncrypt.encrypt(passToEncrypt.toString());
                var rsaEncryptedAesKey2 = rsaEncrypt.encrypt(confirmPassToEncrypt.toString());

                var encryptedTransaction = { 
                    skid,
                    firstName: formVal.firstName.value,
                    middleName: formVal.middleName.value,
                    lastName: formVal.lastName.value,
                    email: formVal.email.value,
                    password: rsaEncryptedAesKey.toString(), 
                    confirmPassword: rsaEncryptedAesKey2.toString(), 
                    refId: getRefId ? getRefId : null,
                    recaptchaToken
                };

                dispatch(signUp(encryptedTransaction, getLang));

            }

        } catch (error) {
            setIsLoading(false);
            const errObj = apiErrorHandler(error);

            dispatch(openSnackBar({
                msg: t(errObj.statusText),
                type: 'error'
            }))
        }
    }


    const handleFormSubmit = (e) => {
        e.preventDefault();
        
        if((crfData.data === null && formVal.acceptTnc.value) || (crfData?.data)) {
            handleValidation();
        }
    }

    const handleConsentFieldChange = (index) => {
        setCrfData((prevState) => {
            const updatedCrfData = prevState.data.map((item, idx) => {
                if(idx === index) {
                    return {
                        ...item,
                        checked: !item.checked,
                        ...(item?.msg ? {msg: null} : {})
                    }
                }
                return item;
            });
            return {...prevState, data: updatedCrfData};
        });
    }

    return(
        <form onSubmit={handleFormSubmit} className="cm-auth-form cm-login-form">
            <div className="row">
                <div className="col-lg-4">
                    <div className="form-group mb-3">
                        <label htmlFor="firstName" className='pb-2 text-blue-gray-700 cm-xs-txt fw-medium'>{t("first_name")}*</label>
                        <input type="text" className="form-control text-blue-800 fw-medium" id="firstName" value={formVal.firstName.value} placeholder={t("signup_fname_placeholder")} onChange={(e) => handleFieldChange(e, 'firstName')} />
                        {formVal.firstName.msg !== null && <span className='cm-xs-txt text-danger fw-medium pt-2'>{formVal.firstName.msg}</span>}
                    </div>
                </div>
                <div className="col-lg-4">
                    <div className="form-group mb-3">
                        <label htmlFor="middleName" className='pb-2 text-blue-gray-700 cm-xs-txt fw-medium'>{t("middle_name")}</label>
                        <input type="text" className="form-control text-blue-800 fw-medium" id="middleName" value={formVal.middleName.value} placeholder={t("signup_mname_placeholder")} onChange={(e) => handleFieldChange(e, 'middleName')} />
                        {formVal.middleName.msg !== null && <span className='cm-xs-txt text-danger fw-medium pt-2'>{formVal.middleName.msg}</span>}
                    </div>
                </div>
                <div className="col-lg-4">
                    <div className="form-group mb-3">
                        <label htmlFor="lastName" className='pb-2 text-blue-gray-700 cm-xs-txt fw-medium'>{t("last_name")}</label>
                        <input type="text" className="form-control text-blue-800 fw-medium" id="lastName" value={formVal.lastName.value} placeholder={t("signup_lname_placeholder")} onChange={(e) => handleFieldChange(e, 'lastName')} />
                        {formVal.lastName.msg !== null && <span className='cm-xs-txt text-danger fw-medium pt-2'>{formVal.lastName.msg}</span>}
                    </div>
                </div>
            </div>
            <div className="form-group mb-3">
                <label htmlFor="email" className='pb-2 text-blue-gray-700 cm-xs-txt fw-medium'>{t("login_email_label")}*</label>
                <input type="email" className="form-control text-blue-800 fw-medium" id="email" value={formVal.email.value} placeholder={t("login_email_placeholder")} onChange={(e) => handleFieldChange(e, 'email')} />
                {formVal.email.msg !== null && <span className='cm-xs-txt text-danger fw-medium pt-2'>{formVal.email.msg}</span>}
            </div>
            <div className="form-group mb-3">
                <label htmlFor="password" className='pb-2 text-blue-gray-700 cm-xs-txt fw-medium'>{t("set_a_password")}*</label>
                <div className="cm-icon-field position-relative cm-double-icon-field">
                {formVal.passwordVisible ? <input type={'text'} autocomplete="true" className="form-control text-blue-800 fw-medium" id="password" value={formVal.password.value} placeholder={t("password")} onChange={(e) => handleFieldChange(e, 'password')} />:<input type={'password'} autocomplete="true" className="form-control text-blue-800 fw-medium" id="password" value={formVal.password.value} placeholder={t("password")} onChange={(e) => handleFieldChange(e, 'password')} />}
                    <FontAwesomeIcon icon={formVal.passwordVisible ? regular('eye-slash') : regular('eye')} className={`form-text text-blue-gray-500 position-absolute cm-pass-visiblility-changer`} onClick={() => handlePassVisiblity('passwordVisible')} />
                    <CustomToolTip 
                        title={
                            <>
                                <span className='my-0 cm-sm-txt fw-medium'>{t("your_password_should_be")}</span>
                                <ul className='cm-tooltip-pass-info mt-3 mb-0 ps-3 cm-sm-txt fw-medium'>
                                    <li>{t("pass_point_1")}</li>
                                    <li>{t("pass_point_2")}</li>
                                    <li>{t("pass_point_3")} <span style={{ display: "block"}}>{t("password_allowed_special_characters")}</span></li>
                                    <li>{t("pass_point_4")}</li>
                                </ul>
                            </>
                        }
                    >
                        <FontAwesomeIcon icon={regular("question-circle")} className={`form-text text-blue-gray-500 position-absolute cm-pass-info-icon`} />
                    </CustomToolTip>
                </div>
                
                {formVal.password.msg !== null && <span className='cm-xs-txt text-danger fw-medium pt-2'>{formVal.password.msg}</span>}
            </div>
            <div className="form-group mb-3">
                <label htmlFor="password" className='pb-2 text-blue-gray-700 cm-xs-txt fw-medium'>{t("confirm_password")}*</label>
                <div className="cm-icon-field position-relative cm-double-icon-field">
                {formVal.passwordConfirmVisible ? <input type={'text'} autocomplete="true" className="form-control text-blue-800 fw-medium" id="password-confirm" value={formVal.passwordConfirm.value} placeholder={t("password")} onChange={(e) => handleFieldChange(e, 'passwordConfirm')} /> : <input type={'password'} autocomplete="true" className="form-control text-blue-800 fw-medium" id="password-confirm" value={formVal.passwordConfirm.value} placeholder={t("password")} onChange={(e) => handleFieldChange(e, 'passwordConfirm')} />}
                    <FontAwesomeIcon icon={formVal.passwordConfirmVisible ? regular('eye-slash') : regular('eye')}  className={`form-text text-blue-gray-500 position-absolute cm-pass-visiblility-changer`} onClick={() => handlePassVisiblity('passwordConfirmVisible')} />
                    <CustomToolTip 
                        title={
                            <>
                                <span className='my-0 cm-sm-txt fw-medium'>{t("your_password_should_be")}</span>
                                <ul className='cm-tooltip-pass-info mt-3 mb-0 ps-3 cm-sm-txt fw-medium'>
                                    <li>{t("pass_point_1")}</li>
                                    <li>{t("pass_point_2")}</li>
                                    <li>{t("pass_point_3")} <span style={{ display: "block"}}>{t("password_allowed_special_characters")}</span></li>
                                    <li>{t("pass_point_4")}</li>
                                </ul>
                            </>
                        }
                    >
                        <FontAwesomeIcon icon={regular("question-circle")} className={`form-text text-blue-gray-500 position-absolute cm-pass-info-icon`} />
                    </CustomToolTip>
                </div>
                {formVal.passwordConfirm.msg !== null && <span className='cm-xs-txt text-danger fw-medium pt-2'>{formVal.passwordConfirm.msg}</span>}
            </div>

            <div className={`mt-32 mb-3 cm-signup-tnc-field cm-signup-tnc-field-wrapper ${tncHt && tncHt > tncHtToCheck ? "p-2 border border-blue-gray-100 rounded overflow-auto cm-signup-tnc-scroll" : ""}`} ref={tncRef}>
                {
                    crfData.data && crfData.data.length > 0 ? crfData.data.map((item, index) => (
                        <div key={"crf-field-" + index} className='mb-4 d-flex align-items-start'>
                            {item.checkbox && <div className='mt-1'>
                                <CustomCheckbox
                                    isChecked={item.checked}
                                    handleCheckboxChange={(e) => handleConsentFieldChange(index)}
                                    checkboxId={"mandatory-checkbox-" + index}
                                />
                            </div>}
                            <div className={`text-break ${item.checkbox ? "ps-10" : ""}`}>
                                <div dangerouslySetInnerHTML={{ __html: sanitiseInput(item.consentMessage) }} className='cm-gen-styles text-blue-800'></div>
                                {item.msg && <span className='text-danger text-break cm-xs-txt fw-medium pt-2 d-inline-block'>{t(item.msg)}</span>}
                            </div>
                        </div>
                    )) : <div className="form-group cm-signup-tnc-field">
                        <FormControlLabel control={<Checkbox
                            sx={{
                            color: "#BED3E4",
                            stroke: "#ffffff", 
                            strokeWidth: 1,
                        }}
                            checked={formVal.acceptTnc.value}
                            onChange={() => handleToggleField("acceptTnc")}
                            labelPlacement="end"
                        />} label={<span dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(replaceTranslationStringWithLink(t("signup_tnc"), [{ value: t("terms_of_services"), link: `https://${envState.METTL_URL}/terms-of-services` }, { value: t("license_agreement"), link: `https://${envState.METTL_URL}/license-agreement` }, { value: t("privacy_notice"), link: `https://${envState.METTL_URL}/privacy-notice` }]))}} className='cm-sm-txt user-select-none' />} />
                    </div>
                    
                }
            </div>

            {captchaWidget}
            <div className="cm-form-btn-group mt-4 pt-2 d-flex align-items-center">
                <button onClick={handleFormSubmit} className={`cm-icon-btn position-relative btn btn-primary cm-sm-txt cm-btn cm-prim-btn d-flex align-items-center ${isLoading || (crfData.data === null && !formVal.acceptTnc.value )? 'cm-disable-btn' : ''}`}>
                    <span className="text-white">{t("accept_and_continue")}</span>
                    <FontAwesomeIcon icon={regular('angle-right')} className="ps-2" />
                </button>
                <Link className="btn btn-primary cm-sm-txt cm-btn cm-trans-btn ms-3 text-blue-800" to={`/login${!!getLang ? `?lang=${getLang}` : ''}${getRefId ? `${getLang ? '&' : '?'}refId=${getRefId}` : ''}`}>{t("back_to_login")}</Link>
            </div>
        </form>
    )

}

export default SignupForm;